import {
  MnJudgeAnswerAssessment,
  MnJudgeLatestAssessment,
} from "../../../api/getCalls/fetchMnJudgeAssessment";
import {
  AnswerAssessmentItem,
  AnswerAssessmentVerdict,
  EvaluationCriteria,
  EvaluationDecision,
  MinedInsight,
  MinedInsightTextItem,
  PredictionSummaryData,
} from "./types";

/**
 * Order-level fields needed for the Summary tab that are not on the MN judge payload.
 */
export interface PredictionSummaryOrderMeta {
  cmm_key?: string;
  drug?: string;
  plan?: string;
  form?: string;
}

const VALID_DECISIONS: EvaluationDecision[] = [
  "APPROVE",
  "DENY",
  "PEND",
  "ESCALATE",
];

const VALID_VERDICTS: AnswerAssessmentVerdict[] = [
  "SATISFIES",
  "VIOLATES",
  "INSUFFICIENT",
  "NOT_APPLICABLE",
];

/**
 * Normalizes a raw decision string into a known EvaluationDecision.
 *
 * @param decision - Raw decision from the API
 * @returns Normalized decision, defaulting to PEND when unknown
 */
const normalizeDecision = (decision?: string): EvaluationDecision => {
  const upper = (decision ?? "").toUpperCase() as EvaluationDecision;
  return VALID_DECISIONS.includes(upper) ? upper : "PEND";
};

/**
 * Normalizes a raw verdict string into a known AnswerAssessmentVerdict.
 *
 * @param verdict - Raw verdict from the API
 * @returns Normalized verdict, defaulting to INSUFFICIENT when unknown
 */
const normalizeVerdict = (verdict?: string): AnswerAssessmentVerdict => {
  const upper = (verdict ?? "").toUpperCase() as AnswerAssessmentVerdict;
  return VALID_VERDICTS.includes(upper) ? upper : "INSUFFICIENT";
};

/**
 * Coerces an unknown value into mined insight text items.
 *
 * @param value - Raw array from drug_form / drug_plan
 * @returns Normalized text items
 */
const toInsightTextItems = (value: unknown): MinedInsightTextItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (typeof entry === "string") {
        return { text: entry, corroborated_by: 0 };
      }
      if (entry && typeof entry === "object") {
        const record = entry as Record<string, unknown>;
        const text = String(record.text ?? "");
        if (!text) {
          return null;
        }
        return {
          text,
          corroborated_by: Number(record.corroborated_by ?? 0),
        };
      }
      return null;
    })
    .filter((item): item is MinedInsightTextItem => item !== null);
};

/**
 * Maps answer assessment rows from the API into UI models.
 *
 * @param assessments - Raw answer assessment list
 * @returns Normalized answer assessment items
 */
const mapAnswerAssessments = (
  assessments: MnJudgeAnswerAssessment[] | undefined,
): AnswerAssessmentItem[] => {
  if (!assessments?.length) {
    return [];
  }

  return assessments.map((item) => ({
    q_idx: item.q_idx,
    verdict: normalizeVerdict(item.verdict),
    question: item.question ?? "",
    answer: item.answer ?? "",
  }));
};

/**
 * Maps the criteria block from the API into the Summary/Criteria tab shape.
 *
 * @param criteria - Raw criteria from the latest assessment
 * @returns Normalized evaluation criteria, or null when absent
 */
const mapCriteria = (
  criteria: MnJudgeLatestAssessment["criteria"],
): EvaluationCriteria | null => {
  if (!criteria) {
    return null;
  }

  const text = criteria.text ?? {};
  return {
    source: criteria.source ?? "",
    rxcui: criteria.rxcui ?? "",
    resolution: criteria.resolution ?? "",
    source_id: criteria.source_id ?? "",
    text: {
      indications: text.indications ?? "",
      dosage: text.dosage ?? "",
      contraindications: text.contraindications ?? "",
      limitations: text.limitations ?? "",
    },
  };
};

/**
 * Maps mined insight dicts from the API into the Historical Insights shape.
 *
 * @param minedInsight - Raw mined_insight from the latest assessment
 * @returns Normalized mined insight for the UI, or null when absent
 */
const mapMinedInsight = (
  minedInsight: MnJudgeLatestAssessment["mined_insight"],
): MinedInsight | null => {
  if (!minedInsight) {
    return null;
  }

  const drugFormRaw =
    (minedInsight.drug_form as Record<string, unknown> | null) ?? null;
  const drugPlanRaw =
    (minedInsight.drug_plan as Record<string, unknown> | null) ?? null;

  const drugPlanRule = toInsightTextItems(
    drugPlanRaw?.reasons ?? drugPlanRaw?.rules ?? drugPlanRaw?.drug_plan_rule,
  );

  if (!drugFormRaw) {
    if (!drugPlanRule.length) {
      return null;
    }
    return {
      drug_plan_rule: drugPlanRule,
      drug_form: null,
    };
  }

  return {
    drug_plan_rule: drugPlanRule.length ? drugPlanRule : null,
    drug_form: {
      form: String(drugFormRaw.form ?? ""),
      n_letters: Number(drugFormRaw.n_letters ?? 0),
      classes: Array.isArray(drugFormRaw.classes)
        ? (drugFormRaw.classes as string[])
        : [],
      denial_reasons: toInsightTextItems(
        drugFormRaw.denial_reasons ?? drugFormRaw.common_denial_reasons,
      ),
      step_therapy: toInsightTextItems(drugFormRaw.step_therapy),
      required_labs: toInsightTextItems(
        drugFormRaw.required_labs ?? drugFormRaw.required_docs,
      ),
      source_letters: Array.isArray(drugFormRaw.source_letters)
        ? (drugFormRaw.source_letters as string[])
        : [],
    },
  };
};

/**
 * Maps the latest MN judge assessment plus order metadata into modal view data.
 *
 * @param latest - Latest assessment from GET /pharmacy/mn-judge/assessment/{id}
 * @param orderMeta - Order fields for CMM key / drug / plan / form
 * @returns Prediction summary data for the Evaluation Engine modal
 */
export const mapAssessmentToSummary = (
  latest: MnJudgeLatestAssessment,
  orderMeta: PredictionSummaryOrderMeta = {},
): PredictionSummaryData => {
  return {
    cmm_key: orderMeta.cmm_key ?? "",
    drug: orderMeta.drug ?? "",
    plan: orderMeta.plan ?? "",
    form: orderMeta.form ?? "",
    decision: normalizeDecision(latest.decision),
    confidence: latest.confidence ?? 0,
    confidence_basis: latest.confidence_basis ?? "",
    rationale: latest.rationale ?? "",
    answer_assessment: mapAnswerAssessments(latest.answer_assessment),
    action: latest.recommended_action ?? "",
    prompt_version: latest.model ?? "",
    criteria: mapCriteria(latest.criteria),
    mined_insight: mapMinedInsight(latest.mined_insight),
  };
};
